// src/modules/purityAnalyzer/pages/DeductionSettings.jsx
// HM / Non-HM purity deduction % — the single settings row consumed by
// Run Test to auto-subtract from the calculated purity.

import React, { useEffect, useState } from 'react';
import { Card, Typography, Form, InputNumber, Button, message, Row, Col } from 'antd';
import { SettingOutlined, SaveOutlined } from '@ant-design/icons';
import { fetchDeductionSettings, updateDeductionSettings } from '../api';
import { usePermissions } from '../../../hooks/usePermissions';

const { Title, Text } = Typography;

export default function DeductionSettings() {
  const { can } = usePermissions();
  const canManage = can('purityAnalyzer', 'manageDeductionSettings');

  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLoading(true);
    fetchDeductionSettings()
      .then((res) => {
        const data = res?.data?.data || {};
        form.setFieldsValue({
          hmDeductionPercent: data.hmDeductionPercent ?? 0,
          nonHmDeductionPercent: data.nonHmDeductionPercent ?? 0,
        });
      })
      .catch(() => message.error('Failed to load deduction settings'))
      .finally(() => setLoading(false));
  }, [form]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      await updateDeductionSettings(values);
      message.success('Deduction settings saved');
    } catch (err) {
      if (err?.errorFields) return;
      message.error(err?.response?.data?.message || 'Failed to save deduction settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: 16, maxWidth: 640 }}>
      <Title level={3} style={{ marginTop: 0 }}>
        <SettingOutlined style={{ marginRight: 10 }} />
        Configure
      </Title>
      {/* <Text type="secondary">
        Set the % deducted from the calculated purity for hallmarked (HM) and non-hallmarked
        (Non-HM) samples. On the Run Test screen, the operator toggles HM / Non-HM and the
        matching deduction is subtracted from the calculated purity automatically.
      </Text> */}

      <Card style={{ marginTop: 20 }} loading={loading}>
        <Form form={form} layout="vertical" disabled={!canManage}>
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item
                name="hmDeductionPercent"
                label="HM (%)"
                rules={[{ required: true, message: 'Enter HM deduction %' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} precision={2} size="large" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item
                name="nonHmDeductionPercent"
                label="Non-HM (%)"
                rules={[{ required: true, message: 'Enter Non-HM deduction %' }]}
              >
                <InputNumber style={{ width: '100%' }} min={0} max={100} step={0.1} precision={2} size="large" />
              </Form.Item>
            </Col>
          </Row>
          {canManage && (
            <Button type="primary" icon={<SaveOutlined />} onClick={onSave} loading={saving}>
              Save Settings
            </Button>
          )}
        </Form>
      </Card>
    </div>
  );
}
